// ./src/components/features/EventGuests/GuestGroupAssignments.tsx
//
// Assign a guest to at most one group within each of the event's group sets
// (Teams, Guest type, …). Renders one picker per set, seeded from the guest's
// current group ids. The value is a flat list of selected group ids (the API's
// guestGroupIds — replace semantics).
//
// Each picker is a SelectField, so on phones it opens the
// full-screen picker instead of an inline dropdown (which pushed the drawer's
// scroll position around and left you stranded), and it offers create-in-place
// through the REAL `GuestGroupDrawer` (name, description, colour, icon, member
// cap) — never a reduced one-off form. A set with no groups yet still renders, so
// the FIRST group can be added right here.

import React, { useState } from "react";
import { Stack, Text, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { GuestGroup, GuestGroupSet } from "@/utils/api";
import { useGuestGroupSets, useGuestGroups } from "@/hooks";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { ColorIconBadge, ErrorAlert, SelectField } from "@/components/generic";
import { GuestGroupDrawer } from "@/pages/private/EventGuestGroupsPage/GuestGroupDrawer";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_FOR_GUEST_EDITORS } from "@/utils/createInPlace";

interface SetGroupSelectProps {
  set: GuestGroupSet;
  /** The full flat list of selected group ids across all sets. */
  value: string[];
  /** Replace this set's previously-selected group (prev) with next (null clears). */
  onReplace: (prev: string | null, next: string | null) => void;
}

const SetGroupSelect: React.FC<SetGroupSelectProps> = ({ set, value, onReplace }) => {
  const { t } = useTranslation(["events", "common"]);
  const { groups, loading, error, reload } = useGuestGroups(set.id);
  const [createOpen, setCreateOpen] = useState(false);
  // #142 — a crew member adds a group only with `guests.edit` (the server's rule for an event's group set).
  const canCreate = useCreateInPlace(CREATE_FOR_GUEST_EDITORS);

  const current = value.find((id) => groups.some((g) => g.id === id)) ?? null;

  const data = groups.map((g) => ({ value: g.id as string, label: g.name ?? "—" }));

  // Append a newly-created group after the existing ones.
  const nextOrderNo = groups.reduce((max, g) => Math.max(max, g.orderNo ?? 0), 0) + 1000;

  // ⚠ A failed read must not offer the select: it would show "No group" for a guest who is in one, and a pick would ADD
  // a second group of this set instead of replacing the first (the current one is unknown).
  if (error) {
    return <ErrorAlert error={error} title={t("events:guests.groups.groupsLoadError", { defaultValue: "Couldn’t load the groups of {{name}}", name: set.name ?? "" })} page="Guests" operation="guestGroupList" />;
  }

  return (
    <>
      <SelectField
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        label={
          <Group gap={6} wrap="nowrap">
            <ColorIconBadge color={set.color} icon={set.icon} size={16} fallbackIcon="UsersGroup" />
            <Text span size="md" fw={500}>
              {set.name}
            </Text>
          </Group>
        }
        placeholder={
          loading
            ? t("common:loading", { defaultValue: "Loading…" })
            : t("events:guests.groups.none", { defaultValue: "No group" })
        }
        data={data}
        value={current}
        onChange={(_n, next) => onReplace(current, next)}
        disabled={loading}
        createLabel={canCreate ? t("events:guests.groups.createOption", { defaultValue: "Create a group" }) : undefined}
        onCreate={canCreate ? () => setCreateOpen(true) : undefined}
        clearable
        searchable={groups.length > 8}
        size="md"
      />

      {/* The real group editor — same one the Guest groups page opens. */}
      <GuestGroupDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        guestGroupSetId={set.id as string}
        group={null}
        defaultOrderNo={nextOrderNo}
        onSaved={reload}
        onCreated={(created: GuestGroup) => {
          if (created?.id) {
            onReplace(current, created.id);
          }
        }}
      />
    </>
  );
};

export interface GuestGroupAssignmentsProps {
  eventId: string;
  /** Flat list of assigned group ids (one per set). */
  value: string[];
  onChange: (ids: string[]) => void;
}

export const GuestGroupAssignments: React.FC<GuestGroupAssignmentsProps> = ({ eventId, value, onChange }) => {
  const { t } = useTranslation(["events"]);
  const { sets, isLoaded, error } = useGuestGroupSets(eventId);

  const handleReplace = (prev: string | null, next: string | null) => {
    const kept = value.filter((id) => id !== prev);
    onChange(next ? [...kept, next] : kept);
  };

  // A failed read is not "No guest groups defined for this event yet".
  if (error) {
    return <ErrorAlert error={error} title={t("events:guests.groups.setsLoadError", { defaultValue: "Couldn’t load the guest groups" })} page="Guests" operation="guestGroupSetList" />;
  }

  if (isLoaded && sets.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {t("events:guests.groups.noSets", {
          defaultValue: "No guest groups defined for this event yet.",
        })}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {sets.map((set) => (
        <SetGroupSelect key={set.id} set={set} value={value} onReplace={handleReplace} />
      ))}
    </Stack>
  );
};
